import {
  useCountryAllWarehouseListQuery,
  useCountryByIdQuery,
  usePurchaseOrderByIdQuery,
  usePurchaseOrderShippingClientAddressQuery,
  useUpdatePurchaseOrderShippingMutation,
} from 'api';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';

interface UseShippingEditProps {
  onCancelModal: () => void;
  open: boolean;
}

export const useShippingEdit = (props: UseShippingEditProps) => {
  const { onCancelModal, open } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data = {} } = usePurchaseOrderByIdQuery({ id });
  const { purchaseOrder = {} } = data;

  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);

  const { data: countryData } = useCountryByIdQuery({ countryId: selectedCountryId }, { skip: !selectedCountryId });
  const { data: warehouseData = [] } = useCountryAllWarehouseListQuery();
  const { data: clientShippingInfo } = usePurchaseOrderShippingClientAddressQuery(
    { purchaseOrderId: id },
    { skip: !id }
  );
  const [updatePurchaseOrderShipping, { isLoading: isLoadingUpdate }] = useUpdatePurchaseOrderShippingMutation();

  const initialShipping: Record<string, any> = useMemo(
    () => ({
      shippingType: purchaseOrder?.shippingInformation?.shippingType,
      countryId: purchaseOrder?.shippingInformation?.country?.id,
      shippingCost: purchaseOrder?.shippingInformation?.shippingCost,
      countryWarehouseId: purchaseOrder?.shippingInformation?.countryWarehouseId,
      toWarehouse: purchaseOrder?.shippingInformation ? purchaseOrder?.shippingInformation?.toWarehouse : true,
    }),
    [purchaseOrder]
  );
  const onValuesChange = useCallback(
    async (values: Record<string, any>) => {
      setDisableSaveBtn(false);
      if ('toWarehouse' in values && !values.toWarehouse) {
        setDisableSaveBtn(!!clientShippingInfo?.errorType);
      }
    },
    [clientShippingInfo?.errorType]
  );
  const states = useMemo(() => {
    if (!countryData?.states) return [];
    return countryData.states;
  }, [countryData]);

  const handleCountryChange = useCallback(
    (countryId: string) => {
      setSelectedCountryId(countryId);
      // Clear state when country changes
      form.setFieldValue('state', undefined);
    },
    [form]
  );

  const onCancel = useCallback(() => {
    onCancelModal();
    form.resetFields();
  }, [form, onCancelModal]);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialShipping[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }
      if (payload.toWarehouse) {
        payload.toWarehouse = true;
      } else {
        payload.shippingAddressId = clientShippingInfo?.shippingAddress?.id;
        payload.toWarehouse = false;
      }
      payload.id = id as string;
      const { data, error }: Record<string, any> = await updatePurchaseOrderShipping(payload);
      if (data?.success) {
        handleSuccess('Purchase Order shipping information was successfully updated.');
        onCancelModal();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [
    updatePurchaseOrderShipping,
    handleSuccess,
    handleError,
    id,
    onCancelModal,
    onCancel,
    form,
    initialShipping,
    clientShippingInfo,
  ]);

  useEffect(() => {
    if (open && initialShipping) {
      form.setFieldsValue(initialShipping);
      if (initialShipping.countryId) {
        setSelectedCountryId(initialShipping.countryId);
      }
    }
  }, [form, open, initialShipping]);

  return {
    form,
    isLoadingUpdate,
    onFinish,
    onCancel,
    states,
    handleCountryChange,
    warehouseData,
    clientShippingInfo,
    initialShipping,
    disableSaveBtn,
    onValuesChange,
  };
};
